<template>
  <!-- Aviso persistente para grace/lapsed, sin botón de cerrar. -->
  <!-- daysLeft viene negativo; acá se muestra en positivo. -->
  <div class="sub-banner" :class="`sub-banner--${tone}`" role="status">
    <PhClockCountdown v-if="isGrace" :size="18" weight="regular" />
    <PhLockSimple v-else :size="18" weight="regular" />
    <span class="sub-banner--text">
      {{ $t(isGrace ? 'renewal.grace.message' : 'renewal.lapsedBanner.message', { days: daysAgo }) }}
    </span>
    <a class="sub-banner--cta" :href="mailtoHref">{{ $t('renewal.grace.cta') }}</a>
  </div>
</template>

<script>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useSubscription } from '@/composables/useSubscription';
import { useAuth } from '@/composables/useAuth';
import { PhClockCountdown, PhLockSimple } from '@phosphor-icons/vue';
import { SUPPORT_EMAIL } from '@/config/contact';

export default {
  name: 'SubscriptionStatusBanner',
  components: { PhClockCountdown, PhLockSimple },
  setup() {
    const { t } = useI18n();
    const { company } = useAuth();
    const { subscriptionDaysLeft, subscriptionType, isSubscriptionInGrace } = useSubscription();

    const isGrace = computed(() => isSubscriptionInGrace.value);
    const tone = computed(() => (isGrace.value ? 'warning' : 'danger'));

    // daysLeft es negativo en grace/lapsed ("venció hace 3 días" == -3).
    const daysAgo = computed(() => Math.abs(subscriptionDaysLeft.value ?? 0));

    const mailtoHref = computed(() => {
      const subject = t('renewal.mailto.subject', { company: company.value?.name || '' });
      const body = t('renewal.mailto.body', {
        company: company.value?.name || '',
        plan: subscriptionType.value || '—',
        date: '',
      });
      return `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    });

    return { isGrace, tone, daysAgo, mailtoHref };
  },
};
</script>

<style scoped lang="scss">
.sub-banner {
  position: sticky;
  top: 0;
  z-index: 900; // debajo del overlay (1000), sobre el resto del layout
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--space-sm, 8px);
  padding: 10px 16px;
  color: var(--text-default);
  font-size: 13px;
  text-align: center;
  flex-wrap: wrap;

  &--warning {
    background: color-mix(in oklab, var(--warning-500) 16%, var(--surface-elevated));
    border-bottom: 1px solid color-mix(in oklab, var(--warning-500) 35%, transparent);
  }
  &--danger {
    background: color-mix(in oklab, var(--error-500) 16%, var(--surface-elevated));
    border-bottom: 1px solid color-mix(in oklab, var(--error-500) 35%, transparent);
  }
}

.sub-banner--cta {
  font-weight: 700;
  color: var(--accent);
  text-decoration: underline;
  white-space: nowrap;
}
</style>
